import React, { useState, useRef, useEffect } from 'react';
import { Trash2, Eye, MoreVertical, CheckSquare, Play } from 'lucide-react';

interface ActionButtonsProps {
    onDelete: () => void;
    onPreview: () => void;
    onMarkAllDone: () => void;
    /** Whether to show the preview button (default: true). Hide when inline preview pane is visible. */
    showPreviewButton?: boolean;
    /** Callback when activate button is clicked */
    onActivate?: () => void;
    /** Whether the activate button should be shown (true when progress is 100%) */
    canActivate?: boolean;
    /** Tooltip text explaining why activation is disabled */
    activateDisabledReason?: string;
}

/**
 * Action buttons for the maintenance draft page header
 * Includes delete, preview buttons, and a more options menu
 */
export const ActionButtons: React.FC<ActionButtonsProps> = ({
    onDelete,
    onPreview,
    onMarkAllDone,
    showPreviewButton = true,
    onActivate,
    canActivate = false,
    activateDisabledReason
}) => {
    const [showMenu, setShowMenu] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);

    // Close menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setShowMenu(false);
            }
        };

        if (showMenu) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showMenu]);

    return (
        <div className="action-buttons-group">
            <div className="action-more-wrapper" ref={menuRef}>
                <button
                    className="btn btn-outline-secondary action-more-btn"
                    onClick={() => setShowMenu(!showMenu)}
                    title="Fler alternativ"
                >
                    <MoreVertical size={20} />
                </button>
                {showMenu && (
                    <div className="action-dropdown-menu">
                        <button
                            className="action-dropdown-item"
                            onClick={() => {
                                setShowMenu(false);
                                onMarkAllDone();
                            }}
                        >
                            <CheckSquare size={16} className="me-2" />
                            Markera alla som klara
                        </button>
                        <button
                            className="action-dropdown-item action-dropdown-item-danger"
                            onClick={() => {
                                setShowMenu(false);
                                onDelete();
                            }}
                        >
                            <Trash2 size={16} className="me-2" />
                            Radera utkast
                        </button>
                    </div>
                )}
            </div>
            {showPreviewButton && (
                <button
                    className="btn btn-primary"
                    onClick={onPreview}
                    title="Förhandsgranska underhållsplan"
                >
                    <Eye size={20} className="me-2" />
                    Förhandsgranska
                </button>
            )}
            {canActivate && onActivate && (
                <button
                    className="btn btn-success activate-plan-btn"
                    onClick={onActivate}
                    disabled={!!activateDisabledReason}
                    title={activateDisabledReason || 'Aktivera underhållsplan'}
                >
                    <Play size={20} className="me-2" />
                    Aktivera underhållsplan
                </button>
            )}
        </div>
    );
};
